import { assert } from '@ember/debug';
import { action } from '@ember/object';
import { isEqual } from '@ember/utils';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ComponentLike } from '@glint/template';
import { modifier } from 'ember-modifier';
import PowerSelect from 'ember-power-select/components/power-select';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
// eslint-disable-next-line import-x/no-named-as-default
import emberPowerSelectIsSelected from 'ember-power-select/helpers/ember-power-select-is-selected';
import { countOptions } from 'ember-power-select/utils/group-utils';
import { and, gt, not } from 'ember-truth-helpers';

import type {
  CollapsedGroup,
  DisabledArgs,
  Group,
  Option,
} from '../../types/components/prism/combo-select-input.ts';
import type { IconName } from '../../types/components/prism/icon.ts';
import PrismComboSelectInputMultipleSelectionBeforeOptions from './combo-select-input/multiple-selection-before-options.gts';
import PrismComboSelectInputMultipleSelectionOption from './combo-select-input/multiple-selection-option.gts';
import PrismComboSelectInputMultipleSelectionTrigger from './combo-select-input/multiple-selection-trigger.gts';
import PrismComboSelectInputOption from './combo-select-input/option.gts';
import PrismComboSelectInputSearch from './combo-select-input/search.gts';
import PrismComboSelectInputTrigger from './combo-select-input/trigger.gts';
import PrismComboSelectInputUnsafeAdditionalAction from './combo-select-input/unsafe/additional-action.gts';
import PrismComboSelectInputUnsafeCollapsibleGroup from './combo-select-input/unsafe/collapsible-group.gts';
import PrismComboSelectInputUnsafeCreateWhenNoMatchesAction from './combo-select-input/unsafe/create-when-no-matches-action.gts';
import PrismComboSelectInputUnsafeInfiniteScrollOptions from './combo-select-input/unsafe/infinite-scroll-options.gts';
import PrismComboSelectInputUnsafeMultipleSelectionGroup from './combo-select-input/unsafe/multiple-selection-group.gts';
import PrismComboSelectInputUnsafeMultipleSelectionImmediateTrigger from './combo-select-input/unsafe/multiple-selection-immediate-trigger.gts';
import PrismComboSelectInputUnsafeNavigationTrigger from './combo-select-input/unsafe/navigation-trigger.gts';
import PrismTooltip from './tooltip.gts';

interface UnsafeArgs {
  // Used to display an additional action below the options
  unsafe_additionalAction?: {
    buttonType?:
      | 'primary'
      | 'secondary'
      | 'critical'
      | 'critical-secondary'
      | 'warning'
      | 'success'
      | 'intelligence'
      | 'intelligence-secondary'
      | 'brand'
      | 'ghost'
      | 'unstyled';
    dataTestId?: string;
    iconBefore?: IconName;
    isDisabled?: boolean;
    label: string;
    onClick: () => void;
  };
  // This component is highly dynamic and very hard to type, I haven't found a proper solution for it yet
  // Hence why it is just a generic component that accepts any argument
  /* eslint-disable @typescript-eslint/no-explicit-any */
  unsafe_afterBackOptionComponent?: ComponentLike<any>;
  unsafe_collapsibleGroupBackLabel?: string;
  // Used to display an additional create action below the options, but only when nothing matches the search
  unsafe_createWhenNoMatchesAction?: {
    buildLabelWithSearchText: (searchText: string) => string;
    dataTestId?: string;
    onClick: (searchText: string) => void;
  };
  // Mainly used to set a max width, in some select places
  unsafe_dropdownClass?: string;
  unsafe_hasDropdownMaxHeight?: boolean;
  // Only for testing purposes
  unsafe_initiallyOpened?: boolean;
  unsafe_isClampedToOneLine?: boolean;
  unsafe_isClearable?: boolean;
  // Marked as unsafe because of limited usage and vague pattern
  unsafe_isExcludeMode?: boolean;
  unsafe_isMultipleImmediate?: boolean;
  // Marked as unsafe because of accessibility issues and slightly hacky implementation (@see combo-select-input/unsafe/navigation-trigger)
  unsafe_isNavigationTrigger?: boolean;
  unsafe_isNavigationTriggerActive?: boolean;
  unsafe_isNavigationTriggerSmall?: boolean;
  unsafe_matchTriggerWidth?: boolean;
  unsafe_onLoadMore?: () => void;
  // When true, the dropdown will be rendered in place, as a child DOM element, instead of in a wormhole
  // This is useful when the select is used in a modal or other overflow:hidden container
  // Note: when using unsafe_isNavigationTrigger, this will always be true
  unsafe_renderInPlace?: boolean;
  unsafe_selectAllWarningMessage?: string;
  // Could be any type of component, hence the explicit any type
  unsafe_triggerComponent?: ComponentLike<any>;
}

interface MultipleSelectArgs {
  buildLabelWithCount?: (
    count: number,
    config: { unsafe_isExcludeMode?: boolean },
  ) => string;
  canUnselectAll?: boolean; // When true, the additionalAction will be enabled even when no options are selected
  selectAllLabel?: string;
}

interface SearchArgs<Option> {
  isSearchable?: true;
  loadingMessage?: string;
  noMatchesMessage?: string;
  onSearch?: (search: string) => void;
  searchMatcher?: (option: Option, term: string) => number;
  searchMessage?: string;
}

interface PrismComboSelectInputSignatureArgs<
  Value extends Readonly<Option | Option[] | null | undefined>,
  Options extends readonly (Option | Group<Option>)[],
>
  extends
    DisabledArgs,
    SearchArgs<Options[number]>,
    MultipleSelectArgs,
    UnsafeArgs {
  // Used to make the search input focusable when used inside an a11y-dialog.
  // See docs: https://a11y-dialog.netlify.app/advanced/focus-considerations/#advanced-integrations.
  dataA11yDialogIgnoreFocusTrap?: boolean;
  horizontalPosition?: 'left' | 'center' | 'right';
  isSecondary?: boolean;
  isSmall?: boolean;
  onChange: (option: Value, config: { unsafe_isExcludeMode: boolean }) => void;
  onClose?: () => void;
  onOpen?: () => void;
  options: Options;
  placeholder?: string;
  pseudo?: 'hover' | 'active' | 'focus';
  value?: Value | null;
}

type IsMultiple<Value> = Value extends [] ? Value : never;

export interface PrismComboSelectInputSignature<
  Value extends Readonly<Option | Option[] | null | undefined>,
  Options extends readonly (Option | Group<Option>)[],
> {
  Args: PrismComboSelectInputSignatureArgs<Value, Options>;
  Element: HTMLDivElement;
}

export default class PrismComboSelectInput<
  Value extends Readonly<Option | Option[] | null | undefined>,
  Options extends readonly (Option | Group<Option>)[],
> extends Component<PrismComboSelectInputSignature<Value, Options>> {
  // Keeps track of which options are expanded
  // Keeps track of what group is expanded
  @tracked expandedGroupIndices: number[] = [];

  // The real value representing which options are selected
  // Used to revert the value when the select is closed
  @tracked internallySelectedOptions = this.isMultipleWithConfirm
    ? ([...(this.args.value as IsMultiple<Value>)] as IsMultiple<Value>)
    : [];

  // The dropdown can be put into exclude mode, which means the onChange handler will receive an extra boolean argument
  // indicating whether the options should be excluded (= reverse logic), this is only useful when building an asynchronous
  // select in combination with a "Select all" option
  @tracked isExcludeMode = this.args.unsafe_isExcludeMode || false;

  // Makes sure the internally selected options are also still updated when the value changes externally
  // instead of just on initial render
  setInternallySelectedOptions = modifier<
    HTMLDivElement,
    [Value | null | undefined],
    unknown
  >((_, [value]): void => {
    if (!this.isMultipleWithConfirm) {
      return;
    }

    this.internallySelectedOptions = [
      ...(value as IsMultiple<Value>),
    ] as IsMultiple<Value>;
  });

  setIsExcludeMode = modifier<HTMLDivElement, [boolean | undefined], unknown>(
    (_, [isExcludeMode]): void => {
      this.isExcludeMode = Boolean(isExcludeMode);
    },
  );

  get additionalAction(): UnsafeArgs['unsafe_additionalAction'] {
    if (this.isMultipleWithConfirm) {
      const options = this.internallySelectedOptions;

      const hasSufficientOptionsSelected =
        this.args.canUnselectAll || options.length > 0;

      return {
        label: 'Apply',
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => {
          this.args.onChange(options as IsMultiple<Value>, {
            unsafe_isExcludeMode: this.isExcludeMode,
          });
        },
        isDisabled: this.isExcludeMode
          ? this.internallySelectedOptions.length === countOptions(this.options)
          : !hasSufficientOptionsSelected,
      };
    }

    return this.args.unsafe_additionalAction;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areAllOptionsCollapsedGroups() {
    return (
      this.options.length > 0 &&
      this.options.every(
        (option) => 'unsafe_isCollapsed' in option && option.unsafe_isCollapsed,
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disabledMessage() {
    return this.args.disabled ? this.args.disabledMessage : undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dropdownClass() {
    const classes = ['prism-combo-select-input__dropdown'];

    if (this.args.unsafe_dropdownClass) {
      classes.push(this.args.unsafe_dropdownClass);
    }

    if (this.args.unsafe_isNavigationTrigger) {
      classes.push(
        'prism-combo-select-input__dropdown--unsafe-is-navigation-trigger',
      );
    }

    if (this.unsafe_hasDropdownMaxHeight) {
      classes.push('prism-combo-select-input__dropdown--has-max-height');
    }

    if (this.isMultipleWithConfirm) {
      classes.push('prism-combo-select-input__dropdown--is-multiple-confirm');
    }

    return classes.join(' ');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMultiple() {
    return Array.isArray(this.args.value);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMultipleWithConfirm() {
    return this.isMultiple && !this.args.unsafe_isMultipleImmediate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get options() {
    assert(
      'You must provide a `unsafe_collapsibleGroupBackLabel` when using collapsible groups',
      this.expandedGroupIndices.length === 0 ||
        this.args.unsafe_collapsibleGroupBackLabel,
    );

    const aGroupIsExpanded = this.expandedGroupIndices.length > 0;
    if (aGroupIsExpanded) {
      // Looks up the expanded group in the options array
      let expandedGroup: Options | Options[number] = this.args.options;
      this.expandedGroupIndices.forEach((index) => {
        // If there's an options object present, that means we're in a group: { groupName: string, options: Option[] }
        if ('options' in expandedGroup) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          expandedGroup = expandedGroup.options;
        }

        // If there's no options object present, that means we're at the array level: Option[]
        expandedGroup = (expandedGroup as Options)[index] as Options[number];
      });

      return [
        { ...expandedGroup, unsafe_isCollapsed: false },
      ] as unknown as Options;
    }

    return this.args.options;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get renderInPlace() {
    // Navigation triggers are always rendered in place in order to make the open/close on hover work
    if (this.args.unsafe_isNavigationTrigger) {
      return true;
    }

    return this.args.unsafe_renderInPlace;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showCreateWhenNoMatchesAction() {
    if (this.args.unsafe_additionalAction) {
      return false;
    }

    return this.args.unsafe_createWhenNoMatchesAction;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get triggerClass() {
    // Custom trigger components don't get styled at all
    if (this.args.unsafe_triggerComponent) {
      return 'prism-combo-select-input__unsafe-trigger-component';
    }

    if (this.args.unsafe_isNavigationTrigger) {
      if (this.args.unsafe_isNavigationTriggerActive) {
        return 'prism-combo-select-input__unsafe-navigation-trigger prism-combo-select-input__unsafe-navigation-trigger--is-active';
      }

      return 'prism-combo-select-input__unsafe-navigation-trigger';
    }

    const classes = ['prism-combo-select-input__trigger'];

    if (this.args.isSecondary) {
      classes.push('prism-combo-select-input__trigger--is-secondary');
    } else {
      classes.push('prism-combo-select-input__trigger--is-default');
    }

    if (this.args.isSmall) {
      classes.push('prism-combo-select-input__trigger--size-small');
    }

    return classes.join(' ');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get triggerComponent() {
    if (this.args.unsafe_triggerComponent) {
      return {
        component: ensureSafeComponent(this.args.unsafe_triggerComponent, this),
        args: {},
      };
    }

    if (this.args.unsafe_isNavigationTrigger) {
      return {
        component: ensureSafeComponent(
          PrismComboSelectInputUnsafeNavigationTrigger,
          this,
        ) as unknown as ComponentLike<unknown>, // not sure why this casting is needed
        args: {
          isActive: this.args.unsafe_isNavigationTriggerActive,
          isSmall: this.args.unsafe_isNavigationTriggerSmall,
        },
      };
    }

    if (this.isMultipleWithConfirm) {
      return {
        component: ensureSafeComponent(
          PrismComboSelectInputMultipleSelectionTrigger,
          this,
        ) as unknown as ComponentLike<unknown>, // not sure why this casting is needed,
        args: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          buildLabelWithCount: (count: number) => {
            if (this.args.buildLabelWithCount) {
              if (this.isExcludeMode) {
                return this.args.buildLabelWithCount(
                  this.internallySelectedOptions.length,
                  {
                    unsafe_isExcludeMode: true,
                  },
                );
              }

              return this.args.buildLabelWithCount(count, {
                unsafe_isExcludeMode: false,
              });
            }

            return undefined;
          },
          isSmall: this.args.isSmall,
          selectAllLabel: this.args.selectAllLabel,
        },
      };
    }

    if (this.args.unsafe_isMultipleImmediate) {
      return {
        component: ensureSafeComponent(
          PrismComboSelectInputUnsafeMultipleSelectionImmediateTrigger,
          this,
        ) as unknown as ComponentLike<unknown>, // not sure why this casting is needed,
        args: {},
      };
    }

    return {
      component: ensureSafeComponent(
        PrismComboSelectInputTrigger,
        this,
      ) as unknown as ComponentLike<unknown>, // not sure why this casting is needed
      args: {
        isSecondary: this.args.isSecondary,
        isSmall: this.args.isSmall,
        unsafe_isClampedToOneLine: this.args.unsafe_isClampedToOneLine,
        unsafe_isClearable: this.args.unsafe_isClearable,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unsafe_hasDropdownMaxHeight() {
    // Navigation triggers are never scrollable
    if (this.args.unsafe_isNavigationTrigger) {
      return false;
    }

    if (this.args.unsafe_hasDropdownMaxHeight === undefined) {
      return true;
    }

    return this.args.unsafe_hasDropdownMaxHeight;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get value() {
    return this.args.value as Option;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueMultiple() {
    if (this.isMultipleWithConfirm) {
      if (this.isExcludeMode) {
        return this.options.filter((option) => {
          return !this.internallySelectedOptions.some(
            (internallySelectedOption) => {
              return isEqual(internallySelectedOption, option);
            },
          );
        }) as IsMultiple<Value>;
      }

      return this.internallySelectedOptions as IsMultiple<Value>;
    }

    return this.args.value as IsMultiple<Value>;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildSelectionForExcludeMode(option: Option) {
    const optionAlreadyExcluded = this.internallySelectedOptions.some(
      (selectedOption) => isEqual(selectedOption, option),
    );
    if (optionAlreadyExcluded) {
      return this.internallySelectedOptions.filter((selectedOption) => {
        return !isEqual(selectedOption, option);
      });
    }

    return [...this.internallySelectedOptions, option];
  }

  @action
  handleChange(option: Option, select: unknown): void {
    // Don't trigger an onChange event when the option is a collapsed group, since
    // that means the user has opened the group, we don't want to update the value
    const optionIsCollapsedGroup = option && 'unsafe_isCollapsed' in option;
    if (optionIsCollapsedGroup) {
      // Look up the index in the root options
      let index = this.options.indexOf(option);

      if (index === -1) {
        // If the option is not in the root options, it means we are in a group
        // Look up the index in the first (and only) group of nested options
        const group = this.options[0] as CollapsedGroup<Option>;
        index = group.options.indexOf(option);
      }

      this.expandedGroupIndices = [...this.expandedGroupIndices, index];
      return;
    }

    this.args.onChange(option as Value, {
      unsafe_isExcludeMode: this.isExcludeMode,
    });
    this.expandedGroupIndices = [];

    // @ts-expect-error select is not typed, since importing the type from our overrides causes a type error in apps that use this package
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    select.actions.close();
  }

  @action
  handleChangeMultiple(option: Option[], select: unknown): void {
    // Don't trigger an onChange event when the option is a collapsed group, since
    // that means the user has opened the group, we don't want to update the value
    const isOneOptionAdded =
      option.length === this.internallySelectedOptions.length + 1;
    const lastOption = option[option.length - 1];
    const lastOptionIsCollapsedGroup =
      lastOption && 'unsafe_isCollapsed' in lastOption;
    if (isOneOptionAdded && lastOptionIsCollapsedGroup) {
      let index = this.options.indexOf(lastOption);

      if (index === -1) {
        // If the option is not in the root options, it means we are in a group
        // Look up the index in the first (and only) group of nested options
        const group = this.options[0] as CollapsedGroup<Option>;
        index = group.options.indexOf(lastOption);
      }

      this.expandedGroupIndices = [...this.expandedGroupIndices, index];
      return;
    }

    if (this.isMultipleWithConfirm) {
      // Option is an array of options in this case
      // We only update the internal options, since only the action
      // should update the value
      this.internallySelectedOptions = [...option] as IsMultiple<Value>;
      return;
    }

    this.args.onChange(option as IsMultiple<Value>, {
      unsafe_isExcludeMode: this.isExcludeMode,
    });

    // @ts-expect-error select is not typed, since importing the type from our overrides causes a type error in apps that use this package
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    select.actions.close();
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleClose() {
    // Always show the root options again when the select is closed
    this.expandedGroupIndices = [];
    // Reset the internally selected options to the root value when the select is closed
    if (this.isMultipleWithConfirm) {
      this.internallySelectedOptions = [
        ...(this.args.value as IsMultiple<Value>),
      ] as IsMultiple<Value>;
      this.isExcludeMode = Boolean(this.args.unsafe_isExcludeMode);
    }

    if (this.args.onClose) {
      return this.args.onClose();
    }

    return undefined;
  }

  @action
  handleGroupCollapseClick(): void {
    // Remove the last entry from the expanded option indices, e.g. going back one level
    this.expandedGroupIndices = this.expandedGroupIndices.slice(0, -1);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleSearch(search: string) {
    if (this.isExcludeMode) {
      this.isExcludeMode = false;
      this.internallySelectedOptions = [];
    }

    if (this.args.onSearch) {
      return this.args.onSearch(search);
    }

    return undefined;
  }

  @action
  handleSelectAllChange(toggle: boolean): void {
    if (!this.isMultipleWithConfirm || !this.args.unsafe_onLoadMore) {
      return;
    }

    this.isExcludeMode = toggle;
    if (toggle) {
      this.internallySelectedOptions = [];
    }
  }

  <template>
    <div
      class="prism-combo-select-input"
      {{this.setInternallySelectedOptions @value}}
      {{this.setIsExcludeMode @unsafe_isExcludeMode}}
    >
      {{#if this.isMultiple}}
        <PowerSelectMultiple
          @afterOptionsComponent={{if
            (and this.additionalAction (not this.areAllOptionsCollapsedGroups))
            (component
              PrismComboSelectInputUnsafeAdditionalAction
              buttonType=this.additionalAction.buttonType
              dataTestId=this.additionalAction.dataTestId
              iconBefore=this.additionalAction.iconBefore
              isDisabled=this.additionalAction.isDisabled
              label=this.additionalAction.label
              onClick=this.additionalAction.onClick
            )
          }}
          @allowClear={{@unsafe_isClearable}}
          @beforeOptionsComponent={{component
            PrismComboSelectInputMultipleSelectionBeforeOptions
            areAllOptionsCollapsedGroups=this.areAllOptionsCollapsedGroups
            isGroupExpanded=(gt this.expandedGroupIndices.length 0)
            isSearchable=@isSearchable
            onGroupCollapseClick=this.handleGroupCollapseClick
            onSelectAllChange=this.handleSelectAllChange
            selectAllLabel=@selectAllLabel
            unsafe_collapsibleGroupBackLabel=@unsafe_collapsibleGroupBackLabel
            unsafe_selectAllWarningMessage=@unsafe_selectAllWarningMessage
          }}
          @buildSelection={{if
            this.isExcludeMode
            this.buildSelectionForExcludeMode
          }}
          @closeOnSelect={{false}}
          @disabled={{@disabled}}
          @dropdownClass={{this.dropdownClass}}
          @groupComponent={{if
            this.isMultipleWithConfirm
            (component
              PrismComboSelectInputUnsafeMultipleSelectionGroup
              onGroupCollapseClick=this.handleGroupCollapseClick
              unsafe_collapsibleGroupBackLabel=@unsafe_collapsibleGroupBackLabel
            )
          }}
          @horizontalPosition={{@horizontalPosition}}
          @initiallyOpened={{@unsafe_initiallyOpened}}
          @loadingMessage={{@loadingMessage}}
          @matchTriggerWidth={{if @unsafe_matchTriggerWidth true false}}
          @matcher={{@searchMatcher}}
          @noMatchesMessage={{@noMatchesMessage}}
          @noMatchesMessageComponent={{if
            this.showCreateWhenNoMatchesAction
            (component
              PrismComboSelectInputUnsafeCreateWhenNoMatchesAction
              buildLabelWithSearchText=@unsafe_createWhenNoMatchesAction.buildLabelWithSearchText
              dataTestId=@unsafe_createWhenNoMatchesAction.dataTestId
              onClick=@unsafe_createWhenNoMatchesAction.onClick
            )
          }}
          @onChange={{this.handleChangeMultiple}}
          @onClose={{this.handleClose}}
          @onOpen={{@onOpen}}
          @options={{this.options}}
          @optionsComponent={{if
            @unsafe_onLoadMore
            (component
              PrismComboSelectInputUnsafeInfiniteScrollOptions
              onLoadMore=@unsafe_onLoadMore
            )
          }}
          @placeholder={{@placeholder}}
          @preventScroll={{true}}
          @renderInPlace={{this.renderInPlace}}
          @search={{if @onSearch this.handleSearch}}
          @searchEnabled={{@isSearchable}}
          @searchField="label"
          @searchMessage={{@searchMessage}}
          @selected={{this.valueMultiple}}
          @triggerClass={{this.triggerClass}}
          @triggerComponent={{component
            this.triggerComponent.component
            buildLabelWithCount=this.triggerComponent.args.buildLabelWithCount
            isActive=this.triggerComponent.args.isActive
            isSecondary=this.triggerComponent.args.isSecondary
            isSmall=this.triggerComponent.args.isSmall
            selectAllLabel=this.triggerComponent.args.selectAllLabel
            unsafe_isClampedToOneLine=this.triggerComponent.args.unsafe_isClampedToOneLine
            unsafe_isClearable=this.triggerComponent.args.unsafe_isClearable
          }}
          data-test-pseudo={{@pseudo}}
          {{! Passed before all other properties to disallow overwriting internally set properties, only meant for modifiers/data-test-ids }}
          ...attributes
          {{!
      Even though the .component property is already a safe component in the backing class, we still need to apply it here as well
      to avoid embroider build failures
    }}
          {{! Enabled manual control of when to close the dropdown, required for unsafe_collapsedOptions }}
          as |option select|
        >
          {{#if (and this.isMultiple (not @unsafe_isMultipleImmediate))}}
            <PrismComboSelectInputMultipleSelectionOption
              @badge={{option.badge}}
              @badgeType={{option.badgeType}}
              @disabled={{option.disabled}}
              @disabledMessage={{option.disabledMessage}}
              @icon={{option.icon}}
              @label={{option.label}}
              @selected={{emberPowerSelectIsSelected option select.selected}}
              @unsafe_bulletColor={{option.unsafe_bulletColor}}
              @unsafe_subLabel={{option.unsafe_subLabel}}
            />
          {{else}}
            <PrismComboSelectInputOption
              @badge={{option.badge}}
              @badgeType={{option.badgeType}}
              @disabled={{option.disabled}}
              @disabledMessage={{option.disabledMessage}}
              @icon={{option.icon}}
              @iconFlag={{option.iconFlag}}
              @label={{option.label}}
              @selected={{emberPowerSelectIsSelected option select.selected}}
              @unsafe_className={{option.unsafe_className}}
              @unsafe_subLabel={{option.unsafe_subLabel}}
            />
          {{/if}}
        </PowerSelectMultiple>
      {{else}}
        <PowerSelect
          @afterOptionsComponent={{if
            this.additionalAction
            (component
              PrismComboSelectInputUnsafeAdditionalAction
              buttonType=this.additionalAction.buttonType
              dataTestId=this.additionalAction.dataTestId
              iconBefore=this.additionalAction.iconBefore
              isDisabled=this.additionalAction.isDisabled
              label=this.additionalAction.label
              onClick=this.additionalAction.onClick
            )
          }}
          @allowClear={{@unsafe_isClearable}}
          @beforeOptionsComponent={{if
            @isSearchable
            (component
              PrismComboSelectInputSearch
              dataA11yDialogIgnoreFocusTrap=@dataA11yDialogIgnoreFocusTrap
            )
          }}
          @closeOnSelect={{false}}
          @disabled={{@disabled}}
          @dropdownClass={{this.dropdownClass}}
          @groupComponent={{component
            PrismComboSelectInputUnsafeCollapsibleGroup
            onGroupCollapseClick=this.handleGroupCollapseClick
            unsafe_afterBackOptionComponent=@unsafe_afterBackOptionComponent
            unsafe_collapsibleGroupBackLabel=@unsafe_collapsibleGroupBackLabel
          }}
          @horizontalPosition={{@horizontalPosition}}
          @initiallyOpened={{@unsafe_initiallyOpened}}
          @loadingMessage={{@loadingMessage}}
          @matchTriggerWidth={{if @unsafe_matchTriggerWidth true false}}
          @matcher={{@searchMatcher}}
          @noMatchesMessage={{@noMatchesMessage}}
          @noMatchesMessageComponent={{if
            this.showCreateWhenNoMatchesAction
            (component
              PrismComboSelectInputUnsafeCreateWhenNoMatchesAction
              buildLabelWithSearchText=@unsafe_createWhenNoMatchesAction.buildLabelWithSearchText
              dataTestId=@unsafe_createWhenNoMatchesAction.dataTestId
              onClick=@unsafe_createWhenNoMatchesAction.onClick
            )
          }}
          @onChange={{this.handleChange}}
          @onClose={{this.handleClose}}
          @onOpen={{@onOpen}}
          @options={{this.options}}
          @optionsComponent={{if
            @unsafe_onLoadMore
            (component
              PrismComboSelectInputUnsafeInfiniteScrollOptions
              onLoadMore=@unsafe_onLoadMore
            )
          }}
          @placeholder={{@placeholder}}
          @preventScroll={{true}}
          @renderInPlace={{this.renderInPlace}}
          @search={{@onSearch}}
          @searchEnabled={{@isSearchable}}
          @searchField="label"
          @searchMessage={{@searchMessage}}
          @selected={{this.value}}
          @triggerClass={{this.triggerClass}}
          @triggerComponent={{component
            this.triggerComponent.component
            buildLabelWithCount=this.triggerComponent.args.buildLabelWithCount
            isActive=this.triggerComponent.args.isActive
            isSecondary=this.triggerComponent.args.isSecondary
            isSmall=this.triggerComponent.args.isSmall
            selectAllLabel=this.triggerComponent.args.selectAllLabel
            unsafe_isClampedToOneLine=this.triggerComponent.args.unsafe_isClampedToOneLine
            unsafe_isClearable=this.triggerComponent.args.unsafe_isClearable
          }}
          data-test-pseudo={{@pseudo}}
          {{! Passed before all other properties to disallow overwriting internally set properties, only meant for modifiers/data-test-ids }}
          ...attributes
          {{!
      Even though the .component property is already a safe component in the backing class, we still need to apply it here as well
      to avoid embroider build failures
    }}
          {{! We manually manage this state because of collapsed groups and multiple selection with confirmation }}
          as |option select|
        >
          <PrismComboSelectInputOption
            @badge={{option.badge}}
            @badgeType={{option.badgeType}}
            @disabled={{option.disabled}}
            @disabledMessage={{option.disabledMessage}}
            @icon={{option.icon}}
            @iconFlag={{option.iconFlag}}
            @label={{option.label}}
            @selected={{emberPowerSelectIsSelected option select.selected}}
            @unsafe_className={{option.unsafe_className}}
            @unsafe_isLink={{option.unsafe_isLink}}
            @unsafe_subLabel={{option.unsafe_subLabel}}
          />
        </PowerSelect>
      {{/if}}

      {{#if this.disabledMessage}}
        <PrismTooltip @text={{this.disabledMessage}} />
      {{/if}}
    </div>
  </template>
}

export type { Group, Option };
